/**
 * /tools/:slug — single-purpose tool funnel page.
 * Shared layout drives 2 distinct tools (backlink-checker + ai-visibility).
 * Embed mode (?embed=1) strips header/footer for blog embeds.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, Eye, Lock, Sparkles, ChevronRight } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { getToolT } from "../translations-tools";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_ENDPOINTS = {
  "backlink-checker": "/funnel-tools/backlink-quick",
  "ai-visibility-checker": "/funnel-tools/ai-visibility-quick",
};

const scoreToGrade = (s) => {
  if (s >= 95) return "A+"; if (s >= 85) return "A"; if (s >= 75) return "A-";
  if (s >= 65) return "B"; if (s >= 55) return "C"; if (s >= 40) return "D"; return "F";
};
const gradeColor = (s) => {
  if (s >= 80) return "#10B981"; if (s >= 60) return "#F59E0B";
  if (s >= 40) return "#F97316"; return "#EF4444";
};

export default function ToolFunnelPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { language } = useLanguage();
  const embed = params.get("embed") === "1";
  const t = getToolT(language || "en", slug);
  const endpoint = TOOL_ENDPOINTS[slug];

  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [config, setConfig] = useState({ email_gate_mode: "soft", enabled: true });

  useEffect(() => {
    if (!t) return;
    document.title = `${t.title} · TopRankPulse`;
  }, [t]);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/funnel-tools/config`, { params: { slug } })
      .then((r) => setConfig(r.data || {})).catch(() => {});
  }, [slug]);

  const isHardGate = config.email_gate_mode === "hard";

  if (!t || !endpoint) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Tool not found</h1>
          <Link to="/tools" className="text-[#a855f7] underline mt-2 inline-block">← Back to tools</Link>
        </div>
      </div>
    );
  }

  async function runScan(e) {
    e?.preventDefault();
    if (!url.trim()) { setErr(t.err_invalid); return; }
    // In hard mode we still allow submit without email — the server returns
    // a teaser payload with `_email_required:true` and the result UI shows
    // the EmailUnlockOverlay above the masked tiles. This converts better
    // than a hard form-block (visual proof first, capture second).
    setBusy(true); setErr(""); setData(null);
    try {
      const r = await axios.post(`${API}${endpoint}`, {
        url: url.trim(), email: email.trim() || null, language: language || "en",
      }, { timeout: 60000 });
      setData(r.data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || t.err_failed);
    } finally {
      setBusy(false);
    }
  }

  async function submitEmailUnlock(e) {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}${endpoint}`, {
        url: url.trim(), email: email.trim(), language: language || "en",
      }, { timeout: 60000 });
      setData(r.data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || t.err_failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white" data-testid={`tool-page-${slug}`}>
      {!embed && (
        <header className="border-b border-[var(--color-border)] py-4 px-6 flex items-center justify-between">
          <Link to="/" className="font-black text-lg font-['Cabinet_Grotesk']">TopRankPulse</Link>
          <Link to="/tools" className="text-xs text-[var(--color-text-muted)] hover:text-white flex items-center gap-1">
            All tools <ChevronRight className="w-3 h-3" />
          </Link>
        </header>
      )}

      {/* Hero + form */}
      <section className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-xs font-mono tracking-[0.3em] text-[#a855f7] mb-3" data-testid="tool-eyebrow">
          {t.eyebrow}
        </p>
        <h1 className="text-3xl sm:text-5xl font-black font-['Cabinet_Grotesk'] mb-4 leading-tight">{t.h1}</h1>
        <p className="text-sm sm:text-base text-[var(--color-text-muted)] max-w-xl mx-auto mb-8">{t.sub}</p>

        <form onSubmit={runScan} className="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto" data-testid="tool-form">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t.placeholder}
            disabled={busy}
            data-testid="tool-url-input"
            className="flex-1 px-4 py-3 border-2 border-[var(--color-border)] bg-[var(--color-bg)] text-white placeholder-zinc-600 focus:border-[#a855f7] focus:outline-none disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="tool-submit-btn"
            className="px-6 py-3 bg-[#FFE600] text-black font-black uppercase hover:bg-yellow-300 transition disabled:opacity-50"
          >
            {busy ? t.btn_loading : t.btn}
          </button>
        </form>
        {err && (
          <p className="mt-3 text-sm text-red-400 flex items-center justify-center gap-1" data-testid="tool-error">
            <AlertCircle className="w-4 h-4" /> {err}
          </p>
        )}
      </section>

      {/* Result */}
      {data && (
        <section className="max-w-4xl mx-auto px-4 pb-12" data-testid="tool-result">
          {slug === "backlink-checker" && <BacklinkResult data={data} t={t} email={email} setEmail={setEmail} onUnlock={submitEmailUnlock} isHardGate={isHardGate} busy={busy} />}
          {slug === "ai-visibility-checker" && <AIVisibilityResult data={data} t={t} email={email} setEmail={setEmail} onUnlock={submitEmailUnlock} isHardGate={isHardGate} busy={busy} />}

          {/* Universal upgrade CTA */}
          <div className="mt-10 p-6 border-2 border-[#a855f7]/40 bg-gradient-to-br from-[#a855f7]/5 to-transparent rounded-lg text-center">
            <Sparkles className="w-8 h-8 text-[#a855f7] mx-auto mb-3" />
            <p className="text-lg font-bold mb-3">{t.upgrade_msg}</p>
            <a
              href={data.upgrade_cta?.url || "/pricing?upgrade=starter"}
              data-testid="tool-upgrade-btn"
              className="inline-block px-6 py-3 bg-[#a855f7] text-white font-black uppercase hover:bg-purple-600 transition"
            >
              {t.upgrade_btn}
            </a>
          </div>
        </section>
      )}
    </div>
  );
}

// ===== Big-Letter-Grade hero block =====
function BigGrade({ score, label }) {
  const grade = scoreToGrade(score);
  const color = gradeColor(score);
  return (
    <div className="flex flex-col items-center" data-testid="tool-big-grade">
      <span
        className="text-[120px] sm:text-[180px] leading-none font-black font-['Cabinet_Grotesk'] tracking-tight"
        style={{ color }}
        data-testid="tool-grade-letter"
      >
        {grade}
      </span>
      <p className="mt-2 text-2xl font-mono font-bold" style={{ color }}>{score}/100</p>
      {label && <p className="mt-1 text-xs uppercase tracking-wider text-[var(--color-text-muted)]">{label}</p>}
    </div>
  );
}

// ===== Backlink result =====
function BacklinkResult({ data, t, email, setEmail, onUnlock, isHardGate, busy }) {
  const r = data.result || {};
  // In hard mode, server flags `_email_required` until an email is sent.
  // In soft mode the data is fully populated already — no overlay.
  const showEmailGate = !!r._email_required;
  return (
    <div>
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        <BigGrade score={r.score || 0} label={t.score_label} />
        <div className="space-y-4">
          <Stat label={t.estimated_label} value={r.estimated_count ?? 0} big />
          <Stat label={t.da_label} value={r.domain_authority ?? "—"} suffix="/100" />
          <Stat label={t.indexed_label} value={r.indexed_pages == null ? "🔒" : r.indexed_pages} />
          <Stat label={t.mentions_label} value={r.unique_mentioning_domains ?? 0} />
        </div>
      </div>
      <div className="mt-8 p-5 bg-[var(--color-surface)] border border-[var(--color-border)] relative">
        <p className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] mb-3">{t.top_label}</p>
        {(r.top_mentioners || []).length > 0 ? (
          <ul className="grid sm:grid-cols-2 gap-2 text-sm font-mono">
            {r.top_mentioners.map((d, i) => (
              <li key={i} className="text-[var(--color-text-muted)] truncate">· {d}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[var(--color-text-muted)] italic">—</p>
        )}
        {showEmailGate && (
          <EmailUnlockOverlay t={t} email={email} setEmail={setEmail} onUnlock={onUnlock} isHardGate={isHardGate} busy={busy} />
        )}
      </div>
    </div>
  );
}

// ===== AI Visibility result =====
function AIVisibilityResult({ data, t, email, setEmail, onUnlock, isHardGate, busy }) {
  const r = data.result || {};
  const showEmailGate = isHardGate ? r._email_required : false;
  const overall = data.overall_score || 0;
  return (
    <div>
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        <BigGrade score={overall} label={t.score_label} />
        <div className="grid grid-cols-2 gap-3">
          <ScoreTile slug="chatgpt" label={t.chatgpt_label} value={r.chatgpt_ready} />
          <ScoreTile slug="perplexity" label={t.perplexity_label} value={r.perplexity_ready} />
          <ScoreTile slug="gemini" label={t.gemini_label} value={r.gemini_ready} />
          <ScoreTile slug="voice" label={t.voice_label} value={r.voice_search_score} />
        </div>
      </div>
      {/* Schema/content tiles only render when we actually have data — in
          soft-gate mode they come back null because analyze_seo skips them
          on the quick endpoint, and showing locks would mislead the user. */}
      {(r.schema_markup != null || r.content_quality != null || showEmailGate) && (
        <div className="mt-8 grid sm:grid-cols-2 gap-4 relative">
          {r.schema_markup != null && <Stat label={t.schema_label} value={r.schema_markup} />}
          {r.content_quality != null && <Stat label={t.content_label} value={r.content_quality} />}
          {showEmailGate && (
            <EmailUnlockOverlay t={t} email={email} setEmail={setEmail} onUnlock={onUnlock} isHardGate={isHardGate} busy={busy} />
          )}
        </div>
      )}
    </div>
  );
}

function ScoreTile({ label, value, slug }) {
  const v = value;
  const display = v == null ? "🔒" : v;
  const color = typeof v === "number" ? gradeColor(v) : "var(--color-text-muted)";
  const tid = slug ? `tool-score-tile-${slug}` : "tool-score-tile";
  return (
    <div data-testid={tid} className="border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-center">
      <p className="text-3xl font-black font-mono" style={{ color }}>{display}</p>
      <p className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] mt-1">{label}</p>
    </div>
  );
}

function Stat({ label, value, suffix, big }) {
  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <p className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] mb-1">{label}</p>
      <p className={`font-black font-['Cabinet_Grotesk'] ${big ? "text-3xl" : "text-xl"}`}>
        {value}{suffix && <span className="text-base text-[var(--color-text-muted)]">{suffix}</span>}
      </p>
    </div>
  );
}

function EmailUnlockOverlay({ t, email, setEmail, onUnlock, isHardGate, busy }) {
  return (
    <form
      onSubmit={onUnlock}
      className="absolute inset-0 z-10 flex flex-col items-center justify-center backdrop-blur-md bg-[var(--color-bg)]/80 border-2 border-dashed border-[#a855f7]/40 p-6"
      data-testid="tool-email-overlay"
    >
      <Lock className="w-6 h-6 text-[#a855f7] mb-2" />
      <p className="text-sm font-bold mb-3 text-center">
        {isHardGate ? t.email_hard : t.email_soft}
      </p>
      <div className="flex gap-2 w-full max-w-md">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@..."
          required
          data-testid="tool-email-soft"
          className="flex-1 px-3 py-2 border-2 border-[var(--color-border)] bg-[var(--color-bg)] text-white placeholder-zinc-600 focus:border-[#a855f7] focus:outline-none"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="tool-email-submit"
          className="px-4 py-2 bg-[#a855f7] text-white font-bold text-sm hover:bg-purple-600 transition disabled:opacity-50"
        >
          {t.email_btn}
        </button>
      </div>
      <p className="text-[10px] text-[var(--color-text-muted)] mt-2">{t.privacy}</p>
    </form>
  );
}
